// Platform-admin commands. Backed by /admin/* on the backend, which requires
// the `platform:admin` permission (granted to JWT auth when the WorkOS email is
// in PLATFORM_ADMIN_EMAILS). All reads are audit-logged and rate-limited.

import { Command } from 'commander';
import { join, relative } from 'path';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getFormat } from '../helpers.js';
import { findGitRoot } from '../config-as-code/workspace.js';
import { loadTemplateManifest, buildPushBody } from '../templates/manifest.js';
import { writeFileMap, FileCollisionError } from '../templates/files.js';
import { TEMPLATE_SLUG_REGEX, isTemplateLocale } from '../templates/types.js';
import type { TemplatePushResponse, TemplateDetail } from '../templates/types.js';
import { HARNESS_SKILL_DIRS } from '../skill-check.js';
import { existsSync } from 'fs';

export const adminCommand = new Command('admin')
  .description('Platform-admin commands (requires platform:admin grant)');

// --- do ---

const doCommand = adminCommand.command('do').description('Inspect live database storage state');

doCommand.command('tables <type> <id>')
  .description('List internal tables and row counts. Types: database | org | user. Pass the owning org via the global --org for a database DO (ids are per-org, #399)')
  .action(async function (this: Command, type: string, id: string) {
    const client = new ApiClient();
    // The owning org rides the global --org (program.ts). A local --org here would be shadowed
    // by it (Commander routes the same-named option to the root), so read the merged value. (#422)
    const org = this.optsWithGlobals()['org'] as string | undefined;
    const qs = org ? `?org=${encodeURIComponent(org)}` : '';
    const data = await client.request<{ type: string; id: string; tables: { name: string; row_count: number }[] }>(
      'GET',
      `/admin/do/${encodeURIComponent(type)}/${encodeURIComponent(id)}/tables${qs}`,
    );
    console.log(formatOutput(data.tables, getFormat(this)));
  });

doCommand.command('read <type> <id> <table>')
  .description('Read rows from an internal table (sensitive columns redacted). Pass the owning org via the global --org for a database DO (ids are per-org, #399)')
  .option('--limit <n>', 'Max rows (default 20, cap 100)', (v: string) => Number(v))
  .option('--row-id <value>', 'Filter to a single row by its id column')
  .action(async function (
    this: Command,
    type: string,
    id: string,
    table: string,
    opts: { limit?: number; rowId?: string },
  ) {
    const client = new ApiClient();
    // Owning org rides the global --org (a local --org would be shadowed by it — #422).
    const org = this.optsWithGlobals()['org'] as string | undefined;
    const params = new URLSearchParams({ table });
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.rowId !== undefined) params.set('row_id', opts.rowId);
    if (org !== undefined) params.set('org', org);

    const data = await client.request<{
      table: string;
      columns: string[];
      rows: unknown[][];
      truncated: boolean;
    }>(
      'GET',
      `/admin/do/${encodeURIComponent(type)}/${encodeURIComponent(id)}/read?${params}`,
    );

    const format = getFormat(this);
    if (format === 'json') {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
    // Reshape columns/rows into row objects for table display.
    const objs = data.rows.map(r => Object.fromEntries(data.columns.map((c, i) => [c, r[i]])));
    console.log(formatOutput(objs, format));
    if (data.truncated) console.log('(truncated — pass --limit to see more)');
  });

doCommand.command('resync <type> <id>')
  .description("Re-emit a database's documents (and relationships) from the DO to ClickHouse, repairing drifted rows. Type must be: database. Pass the owning org via the global --org (ids are per-org, #399)")
  .option('--record_type <name>', 'Limit to a single record_type (documents only)')
  .option('--no-relationships', 'Skip relationships (re-emit documents only)')
  .option('--dry-run', 'Report how many rows would be re-emitted, without writing')
  .action(async function (
    this: Command,
    type: string,
    id: string,
    opts: { record_type?: string; relationships?: boolean; dryRun?: boolean },
  ) {
    const client = new ApiClient();
    // Owning org rides the global --org (a local --org would be shadowed by it — #422).
    const org = this.optsWithGlobals()['org'] as string | undefined;
    const base: Record<string, unknown> = {};
    if (opts.record_type) base['record_type'] = opts.record_type;
    if (opts.relationships === false) base['includeRelationships'] = false;
    if (opts.dryRun) base['dryRun'] = true;

    interface ResyncRun {
      documents: number;
      relationships: number;
      pending: number;
      dryRun: boolean;
      done: boolean;
      cursor: unknown | null;
    }
    const url = `/admin/do/${encodeURIComponent(type)}/${encodeURIComponent(id)}/resync${org ? `?org=${encodeURIComponent(org)}` : ''}`;

    // The backend bounds each invocation to stay under the per-invocation subrequest cap and
    // returns a resume cursor when more rows remain. Loop until done, accumulating totals.
    let documents = 0;
    let relationships = 0;
    let last: ResyncRun | undefined;
    let cursor: unknown | null = null;
    for (;;) {
      const data = await client.request<ResyncRun>('POST', url, cursor ? { ...base, cursor } : base);
      documents += data.documents;
      relationships += data.relationships;
      last = data;
      if (data.dryRun || data.done) break;
      // No forward progress (cursor missing, or buffer full / ClickHouse not draining) — stop
      // rather than spin; the queued rows drain via the DO retry alarm and the run can be re-tried.
      if (!data.cursor || (data.documents === 0 && data.relationships === 0)) break;
      cursor = data.cursor;
    }

    if (getFormat(this) === 'json') {
      console.log(JSON.stringify({ documents, relationships, pending: last!.pending, dryRun: last!.dryRun, done: last!.done }, null, 2));
      return;
    }
    const verb = last!.dryRun ? 'Would re-emit' : 'Re-emitted';
    console.log(`${verb} ${documents} document(s) and ${relationships} relationship(s)`);
    if (!last!.dryRun && !last!.done) {
      console.log(`(stopped before completion — ${last!.pending} row(s) queued; the DO retry alarm will drain them, then re-run to continue)`);
    } else if (!last!.dryRun && last!.pending > 0) {
      console.log(`(${last!.pending} row(s) still queued — the DO retry alarm will drain them)`);
    }
  });

// --- migrate (one-time platform data migrations) ---

const migrateCommand = adminCommand.command('migrate').description('One-time platform data migrations');

interface RekeyResult {
  org_id: string;
  base_id: string;
  status: string;
  total_rows?: number;
  tables?: Record<string, number>;
  imported?: Record<string, number>;
}

migrateCommand.command('do-rekey')
  .description('Recover #399-orphaned bases: copy legacy bare-slug DO state into the per-org composite DO (idempotent). Use the global --org (all of an org\'s bases) and optionally the global --base (one database)')
  .option('--dry-run', 'Report what would migrate, without writing')
  .action(async function (this: Command, opts: { dryRun?: boolean }) {
    const client = new ApiClient();
    // Org and database ride the global --org/--base (program.ts). Local options here would be
    // shadowed by them (Commander routes same-named options to the root), so read the merged
    // values; the global --base is single-valued, so this targets one database at a time. (#422)
    const globals = this.optsWithGlobals() as { org?: string; base?: string };
    const org = globals.org;
    const database = globals.base;
    const body: Record<string, unknown> = {};
    if (opts.dryRun) body['dry_run'] = true;
    if (database) {
      if (!org) throw new Error('--base requires --org');
      body['bases'] = [{ org_id: org, base_id: database }];
    } else if (org) {
      body['org_id'] = org;
    } else {
      throw new Error('provide --org (optionally with --base)');
    }

    const data = await client.request<{ dry_run: boolean; count: number; results: RekeyResult[] }>(
      'POST',
      '/admin/migrate/do-rekey',
      body,
    );

    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
    console.log(formatOutput(data.results, getFormat(this)));
    const migrated = data.results.filter((r) => r.status === 'migrated').length;
    const verb = data.dry_run ? '[dry-run] processed' : 'processed';
    console.log(`${verb} ${data.count} database(s)${data.dry_run ? '' : ` — ${migrated} migrated`}`);
  });

interface OrgSlugResult {
  org_id: string;
  name?: string;
  slug?: string;
  status: string;
  members?: number;
}

migrateCommand.command('org-slugs')
  .description('Backfill the immutable org slug for orgs created before slugs existed (idempotent). Pass the global --org to target a single org (otherwise all non-deleted orgs)')
  .option('--dry-run', 'Report which orgs would be backfilled (and the derived base slug), without writing')
  .action(async function (this: Command, opts: { dryRun?: boolean }) {
    const client = new ApiClient();
    // Target org rides the global --org (a local --org would be shadowed by it — #422).
    const org = this.optsWithGlobals()['org'] as string | undefined;
    const body: Record<string, unknown> = {};
    if (opts.dryRun) body['dry_run'] = true;
    if (org) body['org_id'] = org;

    const data = await client.request<{ dry_run: boolean; count: number; results: OrgSlugResult[] }>(
      'POST',
      '/admin/migrate/org-slugs',
      body,
    );

    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
    console.log(formatOutput(data.results, getFormat(this)));
    const done = data.results.filter((r) => r.status === 'backfilled').length;
    const verb = data.dry_run ? '[dry-run] processed' : 'processed';
    console.log(`${verb} ${data.count} org(s)${data.dry_run ? '' : ` — ${done} backfilled`}`);
  });

// --- report (platform_report triage queue) ---

const adminReportCommand = adminCommand
  .command('report')
  .description('Inspect and triage the platform_report queue (Sentry + CLI bug reports)');

interface PlatformReportRow {
  report_id: string;
  source: 'sentry' | 'cli_report' | 'review' | 'security_audit';
  classification: 'bug' | 'flaky_test' | 'security' | null;
  status: 'pending' | 'grouped' | 'escalated' | 'shipped' | 'contested' | 'addressed' | 'dismissed';
  fingerprint: string;
  gh_issue_url: string | null;
  reporter_email: string | null;
  created_at: string;
  hold_count?: number;
}

adminReportCommand.command('list')
  .description('List reports with optional filters')
  .option('--status <s>', 'pending | grouped | escalated | shipped | contested | addressed | dismissed')
  .option('--source <s>', 'sentry | cli_report | review | security_audit')
  .option('--classification <c>', 'bug | flaky_test | security')
  .option('--limit <n>', 'Max rows (default 50, cap 200)', (v: string) => Number(v))
  .option('--offset <n>', 'Offset for pagination', (v: string) => Number(v))
  .option('--since <iso>', 'Lower bound on created_at (ISO timestamp)')
  .action(async function (
    this: Command,
    opts: { status?: string; source?: string; classification?: string; limit?: number; offset?: number; since?: string },
  ) {
    const client = new ApiClient();
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.source) params.set('source', opts.source);
    if (opts.classification) params.set('classification', opts.classification);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.since) params.set('since', opts.since);
    const qs = params.toString() ? `?${params}` : '';

    const data = await client.request<{ rows: PlatformReportRow[]; total: number }>(
      'GET',
      `/admin/reports${qs}`,
    );

    const format = getFormat(this);
    if (format === 'json') {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
    console.log(formatOutput(data.rows, format));
    console.log(`(${data.rows.length} of ${data.total})`);
  });

adminReportCommand.command('show <id>')
  .description('Show a single report with its group members')
  .action(async function (this: Command, id: string) {
    const client = new ApiClient();
    const data = await client.request<{ report: PlatformReportRow; members: PlatformReportRow[] }>(
      'GET',
      `/admin/reports/${encodeURIComponent(id)}`,
    );
    console.log(JSON.stringify(data, null, 2));
  });

// Local copy: the CLI is a separate package and can't import backend internals
// (mirrors the local PlatformReportRow above).
interface GroupReportSkip {
  report_id: string;
  reason: 'not_found' | 'terminal' | 'grouped_elsewhere' | 'different_gh_issue';
  detail?: string;
}

function describeGroupSkip(s: GroupReportSkip): string {
  switch (s.reason) {
    case 'not_found':
      return 'not found';
    case 'terminal':
      return `terminal (${s.detail ?? 'resolved'})`;
    case 'grouped_elsewhere':
      return `already grouped under ${s.detail ?? 'another canonical'}`;
    case 'different_gh_issue':
      return `tracks a different GitHub issue (${s.detail ?? 'unknown'})`;
    default: {
      // Compile-time exhaustiveness: a new skip reason must be handled here.
      const _exhaustive: never = s.reason;
      return _exhaustive;
    }
  }
}

adminReportCommand.command('group <canonical_id> <member_ids...>')
  .description('Fold member_ids under canonical_id (members must be pending or escalated)')
  .action(async function (this: Command, canonicalId: string, memberIds: string[]) {
    const client = new ApiClient();
    const data = await client.request<{ grouped: number; skipped: GroupReportSkip[] }>(
      'POST',
      `/admin/reports/${encodeURIComponent(canonicalId)}/group`,
      { member_ids: memberIds },
    );
    console.log(`Grouped ${data.grouped} member(s) under ${canonicalId}`);
    for (const s of data.skipped ?? []) {
      console.log(`  skipped ${s.report_id} — ${describeGroupSkip(s)}`);
    }
  });

adminReportCommand.command('ungroup <member_id>')
  .description('Detach a grouped member back to pending (inverse of group)')
  .action(async function (this: Command, memberId: string) {
    const client = new ApiClient();
    const data = await client.request<{ report: PlatformReportRow }>(
      'POST',
      `/admin/reports/${encodeURIComponent(memberId)}/ungroup`,
    );
    console.log(`Detached ${data.report.report_id} → ${data.report.status}`);
  });

adminReportCommand.command('escalate <id>')
  .description('Mark a pending report as escalated and link a GitHub issue')
  .requiredOption('--gh <url>', 'GitHub issue URL the triage routine opened')
  .option('--verdict <text>', 'AI verdict / triage rationale')
  .action(async function (this: Command, id: string, opts: { gh: string; verdict?: string }) {
    const client = new ApiClient();
    const body: Record<string, unknown> = { target: 'escalated', gh_issue_url: opts.gh };
    if (opts.verdict) body['ai_verdict'] = opts.verdict;
    const data = await client.request<{ report: PlatformReportRow; cascaded_report_ids: string[] }>(
      'PATCH',
      `/admin/reports/${encodeURIComponent(id)}/transition`,
      body,
    );
    console.log(`Escalated ${data.report.report_id} → ${opts.gh}`);
  });

adminReportCommand.command('shipped <id>')
  .description('Mark an escalated cli_report as shipped — hand the fix back to the reporter to verify')
  .option('--note <text>', 'What shipped (reporter-facing; keep it reporter-safe — no internal PR/issue refs)')
  .action(async function (this: Command, id: string, opts: { note?: string }) {
    const client = new ApiClient();
    const body: Record<string, unknown> = { target: 'shipped' };
    if (opts.note) body['note'] = opts.note;
    const data = await client.request<{ report: PlatformReportRow }>(
      'PATCH',
      `/admin/reports/${encodeURIComponent(id)}/transition`,
      body,
    );
    console.log(`Report ${data.report.report_id} → ${data.report.status} (awaiting reporter verification)`);
  });

adminReportCommand.command('hold <id>')
  .description('Defer a pending report one triage cycle (records an internal note + bumps hold_count) — silent, no email')
  .requiredOption('--reason <text>', 'Why you are waiting + what would tip the decision (triage-internal; never shown to the reporter)')
  .action(async function (this: Command, id: string, opts: { reason: string }) {
    const client = new ApiClient();
    const data = await client.request<{ report: PlatformReportRow }>(
      'POST',
      `/admin/reports/${encodeURIComponent(id)}/hold`,
      { reason: opts.reason },
    );
    console.log(`Report ${data.report.report_id} held (cycle ${data.report.hold_count ?? '?'}) — remains ${data.report.status}.`);
  });

adminReportCommand.command('addressed <id>')
  .description('Mark an escalated report as addressed (fix shipped)')
  .action(async function (this: Command, id: string) {
    const client = new ApiClient();
    const data = await client.request<{ report: PlatformReportRow; cascaded_report_ids: string[] }>(
      'PATCH',
      `/admin/reports/${encodeURIComponent(id)}/transition`,
      { target: 'addressed' },
    );
    console.log(`Addressed ${data.report.report_id} (cascaded to ${data.cascaded_report_ids.length} group member(s))`);
  });

adminReportCommand.command('dismiss <id>')
  .description('Mark a report as dismissed (not actionable)')
  .requiredOption('--reason <text>', 'Why this report was dismissed')
  .option('--verdict <text>', 'AI verdict / triage rationale')
  .option('--final', 'Make the dismissal non-contestable (the reporter cannot dispute it)')
  .action(async function (this: Command, id: string, opts: { reason: string; verdict?: string; final?: boolean }) {
    const client = new ApiClient();
    const body: Record<string, unknown> = { target: 'dismissed', dismissal_reason: opts.reason };
    if (opts.verdict) body['ai_verdict'] = opts.verdict;
    // --final settles a clearly-wrong reporter immediately, without waiting for the contest cap.
    if (opts.final) body['final'] = true;
    const data = await client.request<{ report: PlatformReportRow; cascaded_report_ids: string[] }>(
      'PATCH',
      `/admin/reports/${encodeURIComponent(id)}/transition`,
      body,
    );
    console.log(`Dismissed ${data.report.report_id} (reason: ${opts.reason})${opts.final ? ' (final — non-contestable)' : ''}`);
  });

// --- template (Templates gallery authoring; #535) ---

const adminTemplateCommand = adminCommand.command('template').description('Author the public Templates gallery (push/pull the rekor slice)');

adminTemplateCommand.command('push <slug>')
  .description('Publish a template from rekor-templates/<slug>/ to the store (reconcile: upsert / no-op / unpublish on draft)')
  .action(async function (this: Command, slug: string) {
    if (!TEMPLATE_SLUG_REGEX.test(slug)) {
      console.error(`Invalid template slug: ${slug}`);
      process.exit(1);
    }
    const gitRoot = findGitRoot();
    if (!gitRoot) {
      console.error('Not inside a git repository — author templates under <repo>/rekor-templates/.');
      process.exit(1);
    }
    const loaded = loadTemplateManifest(gitRoot, slug);
    const body = buildPushBody(loaded);
    const client = new ApiClient();
    const data = await client.request<TemplatePushResponse>('POST', '/admin/templates/push', body);
    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(data, null, 2));
      return;
    }
    console.log(`Template "${data.slug}": ${data.action} (${data.filesWritten} written, ${data.filesDeleted} deleted).`);
    if (loaded.manifest.status !== 'published') {
      console.log(`(status=${loaded.manifest.status} → unpublished from the store)`);
    }
  });

adminTemplateCommand.command('pull <slug>')
  .description('Download a published template from the store for inspection (writes the rekor slice files)')
  .option('--out <dir>', 'Output directory (default ./template-store-pull/<slug>)')
  .option('--lang <locale>', 'Localized variant (en|pt|es); defaults to the template default')
  .option('--force', 'Overwrite existing files in the output dir (default: refuse on any collision)')
  .action(async function (this: Command, slug: string, opts: { out?: string; lang?: string; force?: boolean }) {
    if (!TEMPLATE_SLUG_REGEX.test(slug)) {
      console.error(`Invalid template slug: ${slug}`);
      process.exit(1);
    }
    if (opts.lang !== undefined && !isTemplateLocale(opts.lang)) {
      console.error(`Invalid --lang "${opts.lang}": must be one of en, pt, es.`);
      process.exit(1);
    }
    const client = new ApiClient();
    const qs = opts.lang ? `?locale=${opts.lang}` : '';
    const detail = await client.request<TemplateDetail>('GET', `/admin/templates/${encodeURIComponent(slug)}${qs}`);
    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(detail, null, 2));
      return;
    }
    const outDir = opts.out ?? join('template-store-pull', slug);
    let written: string[];
    try {
      written = writeFileMap(outDir, detail.files, { overwrite: opts.force });
    } catch (err) {
      if (err instanceof FileCollisionError) {
        console.error(`Refusing to overwrite ${err.collisions.length} existing file(s) in ${relative(process.cwd(), outDir) || '.'}/:`);
        for (const f of err.collisions) console.error(`  ${f}`);
        console.error('\nRe-run with --force to overwrite, or --out <dir> to write elsewhere.');
        process.exit(1);
      }
      throw err;
    }
    console.log(`Pulled template "${slug}" → ${relative(process.cwd(), outDir) || '.'}/ (${written.length} file(s)).`);
    console.log(`includes: rekor=${detail.includes.rekor} (resolved locale: ${detail.resolvedLocale})`);
  });

// --- skill (private operator skill install; #535 Phase B) ---

const SKILL_NAME_REGEX = /^[a-z0-9][a-z0-9-]{0,63}$/;

const adminSkillCommand = adminCommand.command('skill').description('Install private platform-operator skills');

adminSkillCommand.command('install [name]')
  .description('Install a private operator skill (default: rekor-admin) into each present harness\'s skills/ dir')
  .action(async function (this: Command, name: string | undefined) {
    const skill = name ?? 'rekor-admin';
    if (!SKILL_NAME_REGEX.test(skill)) {
      console.error(`Invalid skill name: ${skill}`);
      process.exit(1);
    }
    const client = new ApiClient();
    const { files } = await client.request<{ name: string; files: Record<string, string> }>(
      'GET',
      `/admin/skills/${encodeURIComponent(skill)}`,
    );

    // Anchor at the repo root so each harness discovers the skill under its own skills/ dir.
    const root = findGitRoot() ?? process.cwd();
    // Write into each recognized harness dir already present; if none are present, write all of them.
    const present = HARNESS_SKILL_DIRS.filter((dir) => existsSync(join(root, dir)));
    const targets = present.length > 0 ? present : HARNESS_SKILL_DIRS;

    const fileCount = Object.keys(files).length;
    const relDirs = targets.map((harness) => {
      const targetDir = join(root, harness, 'skills', skill);
      // Idempotent update: re-running install is the sanctioned way to refresh an installed skill,
      // so it overwrites by design (unlike template pull's safe-by-default sink — #540).
      writeFileMap(targetDir, files, { overwrite: true });
      return `${relative(root, targetDir)}/`;
    });

    console.log(`Installed skill "${skill}" (${fileCount} file${fileCount === 1 ? '' : 's'}) → ${relDirs.join(', ')}`);
    console.log('Reload your agent (e.g. restart Claude Code) to pick up the skill.');
  });
